import { DateFormatPreference } from "@/enums/date-format";
import { enGB, enUS } from "date-fns/locale";
import type { Locale } from "date-fns";
import moment, { Moment } from "moment-timezone";

export interface DateFormatConfig {
  preference: DateFormatPreference;
  /** moment.js display format (date only) */
  dateMoment: string;
  /** moment.js display format (date + time, 24h) */
  dateTimeMoment: string;
  /** moment.js parse format for custom date-time strings */
  customDateTimeParseMoment: string;
  /** moment.js output format for custom date-time */
  customDateTimeMoment: string;
  /** date-fns format for range pickers and manual input */
  dateFns: string;
  /** MUI date picker adapter locale */
  adapterLocale: string;
  /** Intl locale for toLocaleDateString */
  intlLocale: string;
  /** date-fns locale for AdapterDateFns */
  dateFnsLocale: Locale;
}

const INTERNATIONAL_CONFIG: DateFormatConfig = {
  preference: DateFormatPreference.INTERNATIONAL,
  dateMoment: "DD/MM/YYYY",
  dateTimeMoment: "DD/MM/YYYY H:mm",
  customDateTimeParseMoment: "DD-MM-YYYY H:mm",
  customDateTimeMoment: "DD/MM/YYYY HH:mm",
  dateFns: "dd/MM/yyyy",
  adapterLocale: "en-gb",
  intlLocale: "en-GB",
  dateFnsLocale: enGB,
};

const US_CONFIG: DateFormatConfig = {
  preference: DateFormatPreference.US,
  dateMoment: "MM/DD/YYYY",
  dateTimeMoment: "MM/DD/YYYY H:mm",
  customDateTimeParseMoment: "MM-DD-YYYY H:mm",
  customDateTimeMoment: "MM/DD/YYYY HH:mm",
  dateFns: "MM/dd/yyyy",
  adapterLocale: "en",
  intlLocale: "en-US",
  dateFnsLocale: enUS,
};

/** Format a Date object for display (date only, slash-separated) */
export function formatDateFromDate(
  inputDate: Date | null,
  preference: DateFormatPreference,
): string {
  if (!inputDate) return "";
  const date = inputDate.getDate().toString().padStart(2, "0");
  const month = (inputDate.getMonth() + 1).toString().padStart(2, "0");
  const year = inputDate.getFullYear();
  if (preference === DateFormatPreference.US) {
    return `${month}/${date}/${year}`;
  }
  return `${date}/${month}/${year}`;
}

export function getDateFormatConfig(
  preference: DateFormatPreference,
): DateFormatConfig {
  return preference === DateFormatPreference.US
    ? US_CONFIG
    : INTERNATIONAL_CONFIG;
}

/**
 * Parse date-time values from API / legacy strings.
 * Backend dates use DD-MM-YYYY (e.g. "25-05-2026 16:39") regardless of print-house display preference.
 */
export function parseDateTimeInput(date: Date | string | null | undefined): Moment {
  if (date == null || date === "") {
    return moment.invalid();
  }
  if (date instanceof Date) {
    return moment(date);
  }

  const str = String(date).trim();
  if (!str) {
    return moment.invalid();
  }

  if (str.includes("T") || /^\d{4}-\d{2}-\d{2}/.test(str)) {
    const iso = moment.utc(str);
    if (iso.isValid()) {
      return iso;
    }
  }

  const apiLegacyFormats = [
    "DD-MM-YYYY H:mm",
    "DD-MM-YYYY HH:mm",
    "DD-MM-YYYY H:mm:ss",
    "DD-MM-YYYY HH:mm:ss",
    "DD-MM-YYYY",
    "DD/MM/YYYY H:mm",
    "DD/MM/YYYY HH:mm",
    "DD/MM/YYYY H:mm:ss",
    "DD/MM/YYYY HH:mm:ss",
    "DD/MM/YYYY",
  ];
  for (const fmt of apiLegacyFormats) {
    const parsed = moment.utc(str, fmt, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  const loose = moment.utc(str);
  return loose.isValid() ? loose : moment.invalid();
}

/** Format ISO date string parts into display date (day/month/year order per preference) */
/** Display ISO date (or date-time) in table cells per print-house preference */
export function formatIsoForTableDisplay(
  isoDate: string | undefined | null,
  preference: DateFormatPreference,
): string {
  if (!isoDate) return "";
  return formatIsoDateParts(isoDate, preference) ?? "";
}

/** Display API / legacy date strings in table cells per print-house preference */
export function formatDateForTableDisplay(
  date: Date | string | null | undefined,
  preference: DateFormatPreference,
): string {
  if (date == null || date === "") return "";
  const parsed = parseDateTimeInput(date);
  if (!parsed.isValid()) {
    return String(date);
  }
  return parsed.local().format(getDateFormatConfig(preference).dateMoment);
}

export function formatIsoDateParts(
  isoDateTime: string,
  preference: DateFormatPreference,
): string | undefined {
  if (!isoDateTime) return undefined;
  const datePart = isoDateTime.split("T")[0];
  if (!datePart) return undefined;
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return undefined;
  if (preference === DateFormatPreference.US) {
    return `${month}/${day}/${year}`;
  }
  return `${day}/${month}/${year}`;
}

/** Format ISO date-time into date + time parts for table cells */
export function formatIsoDateTimeSeparate(
  isoDateTime: string,
  preference: DateFormatPreference,
): { formattedDate: string; formattedTime: string } {
  if (!isoDateTime) {
    return { formattedDate: "", formattedTime: "" };
  }
  const [myDate, time] = isoDateTime.split("T");
  if (!myDate || !time) {
    return { formattedDate: "", formattedTime: "" };
  }
  const [hours, minutes] = time.split(":");
  const [year, month, day] = myDate.split("-");
  if (!year || !month || !day) {
    return { formattedDate: "", formattedTime: "" };
  }
  const formattedTime = `${hours}:${minutes}`;
  if (preference === DateFormatPreference.US) {
    return {
      formattedDate: `${month}/${day}`,
      formattedTime,
    };
  }
  return {
    formattedDate: `${day}/${month}`,
    formattedTime,
  };
}

export function formatIsoDateTimeFull(
  isoDateTime: string,
  preference: DateFormatPreference,
): string | undefined {
  if (!isoDateTime) return undefined;
  const datePart = isoDateTime.split("T")[0];
  const timePart = isoDateTime.split("T")[1];
  if (!datePart) return undefined;
  const [year, month, day] = datePart.split("-");
  const hours = timePart?.split(":")[0] ?? "";
  const minutes = timePart?.split(":")[1] ?? "";
  if (!year || !month || !day) return undefined;
  if (preference === DateFormatPreference.US) {
    return `${month}/${day}/${year} : ${hours}:${minutes}`;
  }
  return `${day}/${month}/${year} : ${hours}:${minutes}`;
}

export function formatIsoDateTimeShort(
  isoDateTime: string,
  preference: DateFormatPreference,
): string | undefined {
  if (!isoDateTime) return undefined;
  const datePart = isoDateTime.split("T")[0];
  const timePart = isoDateTime.split("T")[1];
  if (!datePart) return undefined;
  const [year, month, day] = datePart.split("-");
  const hours = timePart?.split(":")[0] ?? "";
  const minutes = timePart?.split(":")[1] ?? "";
  if (!year || !month || !day) return undefined;
  if (preference === DateFormatPreference.US) {
    return `${hours}:${minutes} ${month}/${day}`;
  }
  return `${hours}:${minutes} ${day}/${month}`;
}

export function formatIsoDateHourShort(
  isoDateTime: string,
  preference: DateFormatPreference,
): string | undefined {
  if (!isoDateTime) return undefined;
  const datePart = isoDateTime.split("T")[0];
  const timePart = isoDateTime.split("T")[1];
  if (!datePart) return undefined;
  const [year, month, day] = datePart.split("-");
  const hours = timePart?.split(":")[0] ?? "";
  const minutes = timePart?.split(":")[1] ?? "";
  if (!year || !month || !day) return undefined;
  if (preference === DateFormatPreference.US) {
    return `${month}/${day} ${hours}:${minutes}`;
  }
  return `${day}/${month} ${hours}:${minutes}`;
}
